/**
 * MultiStepForm
 *
 * A component for collecting input through multiple steps in chat messages
 *
 * Primary responsibilities:
 * - Display a multi-step form with progress indicators
 * - Collect and validate user input at each step
 * - Allow navigation between steps
 * - Submit collected data on completion
 */

import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { IconForms, IconArrowRight, IconArrowLeft, IconCheck, IconX } from '@tabler/icons-react'

interface FormField {
  id: string
  label: string
  type: 'text' | 'textarea' | 'select' | 'email' | 'number'
  placeholder?: string
  required?: boolean
  options?: Array<{ value: string; label: string }> // For select fields
  validation?: (value: string) => string | null // Returns error message or null
}

interface FormStep {
  title: string
  description?: string
  fields: FormField[]
}

interface MultiStepFormProps {
  title?: string
  steps: FormStep[]
  onSubmit: (data: Record<string, string>) => void
  className?: string
}

export const MultiStepForm: React.FC<MultiStepFormProps> = ({
  title = 'Form',
  steps,
  onSubmit,
  className
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitted, setSubmitted] = useState(false)

  const handleInputChange = (fieldId: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value
    }))

    // Clear error when field is edited
    if (errors[fieldId]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[fieldId]
        return newErrors
      })
    }
  }

  const validateStep = () => {
    const currentFields = steps[currentStep].fields
    const newErrors: Record<string, string> = {}
    let isValid = true

    currentFields.forEach((field) => {
      // Check required fields
      if (field.required && (!formData[field.id] || formData[field.id].trim() === '')) {
        newErrors[field.id] =
          field.type === 'select' ? 'Please select an option' : 'This field is required'
        isValid = false
        return
      }

      // Check custom validation
      if (field.validation && formData[field.id]) {
        const validationError = field.validation(formData[field.id])
        if (validationError) {
          newErrors[field.id] = validationError
          isValid = false
        }
      }

      // Field-specific validations
      switch (field.type) {
        case 'email':
          if (formData[field.id]) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(formData[field.id])) {
              newErrors[field.id] = 'Please enter a valid email address'
              isValid = false
            }
          }
          break

        case 'number':
          if (formData[field.id]) {
            if (isNaN(Number(formData[field.id]))) {
              newErrors[field.id] = 'Please enter a valid number'
              isValid = false
            }
          }
          break

        case 'text':
          // Check for minimum length if the field is filled
          if (formData[field.id] && formData[field.id].length < 2) {
            newErrors[field.id] = 'Must be at least 2 characters'
            isValid = false
          }
          break
      }
    })

    setErrors(newErrors)
    return isValid
  }

  const goToNextStep = () => {
    if (validateStep()) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1)
      } else {
        // Final step - submit the form
        handleSubmit()
      }
    }
  }

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = () => {
    if (validateStep()) {
      onSubmit(formData)
      setSubmitted(true)
    }
  }

  // Initialize form fields with default empty values
  useEffect(() => {
    const initialData: Record<string, string> = {}
    steps.forEach((step) => {
      step.fields.forEach((field) => {
        if (!initialData[field.id]) {
          initialData[field.id] = ''
        }
      })
    })
    setFormData((prev) => ({ ...initialData, ...prev }))
  }, [steps])

  const renderField = (field: FormField) => {
    const value = formData[field.id] || ''
    const error = errors[field.id]

    const commonProps = {
      id: field.id,
      'aria-invalid': error ? true : undefined,
      'aria-describedby': error ? `${field.id}-error` : undefined
    }

    switch (field.type) {
      case 'textarea':
        return (
          <div className="space-y-2" key={field.id}>
            <Label htmlFor={field.id} className="text-sm font-medium">
              {field.label}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <Textarea
              {...commonProps}
              placeholder={field.placeholder}
              value={value}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              className={cn(error && 'border-red-500')}
            />
            {error && (
              <p className="text-xs text-red-500" id={`${field.id}-error`} role="alert">
                {error}
              </p>
            )}
          </div>
        )

      case 'select':
        return (
          <div className="space-y-2" key={field.id}>
            <Label htmlFor={field.id} className="text-sm font-medium">
              {field.label}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <Select value={value} onValueChange={(value) => handleInputChange(field.id, value)}>
              <SelectTrigger id={field.id} className={cn(error && 'border-red-500')}>
                <SelectValue placeholder={field.placeholder || 'Select an option'} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        )

      default: // text, email, number, etc.
        return (
          <div className="space-y-2" key={field.id}>
            <Label htmlFor={field.id} className="text-sm font-medium">
              {field.label}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </Label>
            <Input
              {...commonProps}
              type={field.type}
              placeholder={field.placeholder}
              value={value}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              className={cn(error && 'border-red-500')}
            />
            {error && (
              <p className="text-xs text-red-500" id={`${field.id}-error`} role="alert">
                {error}
              </p>
            )}
          </div>
        )
    }
  }

  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <IconForms size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">{title}</h3>
        </div>
      </div>

      {/* Progress steps */}
      <div className="px-4 pt-4">
        <div className="mb-4 flex items-center">
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <div
                className={cn(
                  'flex flex-col items-center',
                  index <= currentStep ? 'text-blue-600' : 'text-gray-400'
                )}
              >
                {/* Step circle */}
                <div
                  className={cn(
                    'flex size-8 items-center justify-center rounded-full border-2 text-sm',
                    index < currentStep
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : index === currentStep
                        ? 'border-blue-600 text-blue-600'
                        : 'border-gray-300 text-gray-400'
                  )}
                >
                  {index < currentStep ? <IconCheck size={16} /> : index + 1}
                </div>

                {/* Step title */}
                <span className="mt-1 text-center text-xs">{step.title}</span>
              </div>

              {/* Connector line */}
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'mx-2 h-0.5 flex-1',
                    index < currentStep ? 'bg-blue-600' : 'bg-gray-300'
                  )}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="p-4">
        {!submitted ? (
          <>
            {/* Current step content */}
            <div className="mb-4">
              <h4 className="mb-1 text-base font-medium">{steps[currentStep].title}</h4>
              {steps[currentStep].description && (
                <p className="text-muted-foreground mb-4 text-sm">
                  {steps[currentStep].description}
                </p>
              )}

              <div className="mt-4 space-y-4">
                {steps[currentStep].fields.map((field) => renderField(field))}
              </div>
            </div>

            {/* Navigation buttons */}
            <div className="mt-6 flex items-center justify-between border-t pt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousStep}
                disabled={currentStep === 0}
                className="gap-1"
              >
                <IconArrowLeft size={14} />
                Back
              </Button>

              <Button size="sm" onClick={goToNextStep} className="gap-1">
                {currentStep === steps.length - 1 ? 'Submit' : 'Next'}
                {currentStep < steps.length - 1 && <IconArrowRight size={14} />}
              </Button>
            </div>
          </>
        ) : (
          // Form submitted state
          <div className="flex flex-col items-center py-6">
            <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
              <IconCheck size={24} className="text-green-600 dark:text-green-400" />
            </div>
            <h5 className="mb-1 text-lg font-medium">Submission Successful!</h5>
            <p className="text-muted-foreground text-center text-sm">
              Thank you for completing the form.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
